<script setup lang="ts">
import { useRouteQuery } from "@vueuse/router";
import { onMounted } from "vue";

import { useResetPasswordMutation } from "@/composables/kili/password-recovery/mutations/use-reset-password-mutation";
import { useLayout } from "@/composables/layout/use-layout";
import { useToaster } from "@/composables/layout/use-toaster";
import { toastKiliError } from "@/data/api/kili/common/toast-kili-error";
import { useTypedIntl } from "@/locales/typed-intl";
import router from "@/router/router";
import { ERouteName } from "@/router/router.type";
import { EMixpanelEvent } from "@/services/mixpanel/mixpanel-events.type";
import { MixpanelService } from "@/services/mixpanel/mixpanel-service";

import Message from "primevue/message";

import PasswordCreation from "@/components/authentication/password-creation.vue";
import SelectLocale from "@/components/layout/select-locale.vue";

const { t, k } = useTypedIntl();
const code = useRouteQuery("code");
const email = useRouteQuery("email");
const toast = useToaster();
const { isGlobalLoading } = useLayout();

const { mutateAsync: resetPassword } = useResetPasswordMutation();

async function handleSubmit(password: string) {
  try {
    isGlobalLoading.value = true;
    await resetPassword({
      email: email.value as string,
      newPassword: password,
      recoveryCode: code.value as string,
    });
    toast.add({
      severity: "success",
      detail: t(k.authentication.passwordRecovery.success),
    });

    MixpanelService.trackEvent(EMixpanelEvent.USER_PASSWORD_SET);

    await router.push({ name: ERouteName.LOGIN });
  } catch (e) {
    toastKiliError(e);
  } finally {
    isGlobalLoading.value = false;
  }
}

onMounted(async () => {
  if (!code.value || !email.value || Array.isArray(email.value) || Array.isArray(code.value)) {
    await router.push({ name: ERouteName.LOGIN });
  }
});
</script>

<template>
  <div class="flex flex-row-reverse p-4">
    <SelectLocale />
  </div>

  <div class="flex justify-center my-4">
    <div class="flex flex-col gap-4 w-96">
      <div class="flex justify-center items-center gap-2">
        <img
          class="w-6"
          src="/voltalis-icon.svg"
        />
        <span class="font-semibold">Voltalis</span>
      </div>

      <h4 class="mt-8">
        {{ t(k.authentication.passwordRecovery.newPassword) }}
      </h4>
      <span class="text-text-secondary">
        {{ t(k.authentication.passwordRecovery.subtitle) }}
      </span>

      <Message
        icon="pi pi-info-circle"
        severity="info"
      >
        {{ t(k.authentication.passwordRecovery.message) }}
      </Message>

      <PasswordCreation @submit="handleSubmit($event)" />
    </div>
  </div>
</template>
